import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Stack, Grid, Typography, Box, FormControl, InputLabel,
  Select, MenuItem, Divider, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  LocalHospital as MedicalIcon,
  ContactEmergency as EmergencyIcon,
} from '@mui/icons-material';

function PatientForm({
  patient = null, onSave, onCancel, loading = false, mode = 'add',
}) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alternatePhone: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    address: {
      street: '', city: '', state: '', zipCode: '',
    },
    insurance: {
      provider: '', policyNumber: '', groupNumber: '', copay: '',
    },
    emergencyContact: { name: '', relationship: '', phone: '' },
    bloodType: '',
    primaryPhysician: '',
    referredBy: '',
    status: 'New',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (patient) {
      setForm({
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        email: patient.email || '',
        phone: patient.phone || '',
        alternatePhone: patient.alternatePhone || '',
        dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : '',
        gender: patient.gender || '',
        maritalStatus: patient.maritalStatus || '',
        address: patient.address || {
          street: '', city: '', state: '', zipCode: '',
        },
        insurance: patient.insurance || {
          provider: '', policyNumber: '', groupNumber: '', copay: '',
        },
        emergencyContact: patient.emergencyContact || { name: '', relationship: '', phone: '' },
        bloodType: patient.bloodType || '',
        primaryPhysician: patient.primaryPhysician || '',
        referredBy: patient.referredBy || '',
        status: patient.status || 'New',
        notes: patient.notes || '',
      });
    }
  }, [patient]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Email is invalid';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(form);
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleNestedChange = (parent, field, value) => {
    setForm((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  return (
    <Box component="form" onSubmit={handleSave} sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <PersonIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700}>
            {mode === 'edit' ? 'Edit Patient' : 'Add New Patient'}
          </Typography>
        </Stack>
        <Divider />

        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  required
                  value={form.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  required
                  value={form.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={form.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={form.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Date of Birth"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={form.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    label="Gender"
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                    <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Insurance Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Insurance Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Insurance Provider"
                  fullWidth
                  value={form.insurance.provider}
                  onChange={(e) => handleNestedChange('insurance', 'provider', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Policy Number"
                  fullWidth
                  value={form.insurance.policyNumber}
                  onChange={(e) => handleNestedChange('insurance', 'policyNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Group Number"
                  fullWidth
                  value={form.insurance.groupNumber}
                  onChange={(e) => handleNestedChange('insurance', 'groupNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Copay Amount"
                  type="number"
                  fullWidth
                  value={form.insurance.copay}
                  onChange={(e) => handleNestedChange('insurance', 'copay', e.target.value)}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Emergency Contact */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmergencyIcon color="error" />
              <Typography variant="h6">Emergency Contact</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  fullWidth
                  value={form.emergencyContact.name}
                  onChange={(e) => handleNestedChange('emergencyContact', 'name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Relationship"
                  fullWidth
                  value={form.emergencyContact.relationship}
                  onChange={(e) => handleNestedChange('emergencyContact', 'relationship', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={form.emergencyContact.phone}
                  onChange={(e) => handleNestedChange('emergencyContact', 'phone', e.target.value)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Medical Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MedicalIcon color="primary" />
              <Typography variant="h6">Medical Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Blood Type</InputLabel>
                  <Select
                    value={form.bloodType}
                    onChange={(e) => handleInputChange('bloodType', e.target.value)}
                    label="Blood Type"
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Primary Physician"
                  fullWidth
                  value={form.primaryPhysician}
                  onChange={(e) => handleInputChange('primaryPhysician', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Referred By"
                  fullWidth
                  value={form.referredBy}
                  onChange={(e) => handleInputChange('referredBy', e.target.value)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Address */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Address</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Street Address"
                  fullWidth
                  value={form.address.street}
                  onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  fullWidth
                  value={form.address.city}
                  onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="State"
                  fullWidth
                  value={form.address.state}
                  onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Zip Code"
                  fullWidth
                  value={form.address.zipCode}
                  onChange={(e) => handleNestedChange('address', 'zipCode', e.target.value)}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Notes */}
        <TextField
          label="Notes"
          multiline
          rows={4}
          fullWidth
          value={form.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Additional notes about this patient..."
        />

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : mode === 'edit' ? 'Update Patient' : 'Create Patient'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default PatientForm;
